/**
 * The user's own sources.
 *
 * Workspace-level, not session-level: you paste your lab notes once and they
 * ground every exploration you open afterwards. That is why this has its own
 * localStorage keys and its own budget rather than riding inside a session
 * document — a corpus stored per session would be re-pasted for every idea and
 * would multiply the same text across every stored exploration.
 *
 * Two rules the whole file is built around:
 *
 *  - The corpus is never evicted to make room for explorations, and explorations
 *    are never evicted to make room for the corpus. Two budgets, two ceilings, no
 *    shared victim selection. At its own ceiling an add is REFUSED, with the
 *    arithmetic, rather than quietly displacing something.
 *  - A passage number is a durable reference. The chunker's version is recorded so
 *    that a citation taken under one chunking can never silently resolve to
 *    different text under another.
 */

import { uid } from './id'
// TYPE-ONLY, so `verbatimModuleSyntax` erases the line and no runtime edge is
// added to a module the retrieval path imports.
import type { DocNotice } from './types'

/**
 * What the door to these documents is called, everywhere it is named: the
 * sidebar's row, the palette's row, the sheet's heading and the sentences that
 * send somebody there (D390). It was "Your own sources" at every one of them,
 * written out each time; the owner shortened it, and a name typed at nine
 * places is nine places for it to drift.
 */
export const SOURCES_DOOR = 'Sources'

/**
 * Why a source check cannot run on a model with no web search, and the one
 * thing that still works there — the corpus, preferred over the web. Raised by
 * four presses, the run's check, a lone check, the coach's cue and the resting
 * press, which each carried their own copy of these words (D390).
 */
export const searchlessCheckSentence = (model: string): string =>
  `${model} has no web search, so there is nothing for a source check to read. Your own documents still work — turn on “Prefer my sources” in ⌘K → ${SOURCES_DOOR}.`

/** The corpus's own share of storage. Deliberately well under the explorations'
 *  reservation: this is reference material, and the work is the graph. */
export const CORPUS_CHARS = 250_000
export const DOC_CHARS = 100_000
export const MAX_DOCS = 16
/**
 * The chunker's version.
 *
 * Bumped whenever paragraph splitting changes. A citation recorded under version 1
 * and resolved under version 2 could point at different text and still resolve —
 * which is worse than failing to resolve at all, so a version mismatch drops the
 * link rather than following it.
 */
export const CHUNKER = 1

const MIN_PASSAGE = 320
const MAX_PASSAGE = 1400

const K = {
  index: 'nv.corpus',
  doc: (id: string) => `nv.c.${id}`,
}

export interface CorpusDoc {
  id: string
  title: string
  addedAt: number
  /** Recomputed from the stored text on every read, never trusted from the index:
   *  a stale count is how a budget check comes to believe there is room. */
  chars: number
  passages: number
  chunker: number
}

export interface LoadedDoc extends CorpusDoc {
  text: string
  /** Split lazily by `passagesOf`, so listing does not chunk every document. */
  chunks: string[]
  /** The short forms this document defines, read out of its text by `acronymsOf`
   *  when it is loaded — like `chunks`, derived and never stored, so no stored
   *  format carries them. Optional because a record `engine/liveSources.ts` builds
   *  never went through `loadDoc`; `retrieve` derives it for those the same way. */
  acronyms?: readonly Acronym[]
  /** Set ONLY by `engine/liveSources.ts`, from the DOI of a record it fetched —
   *  nothing a user pastes carries a DOI, so `addDoc` and `loadDoc` below can
   *  never write one and a stored document can never claim one. */
  notice?: DocNotice
}

function get(k: string): string | null {
  try {
    return localStorage.getItem(k)
  } catch {
    return null
  }
}
function put(k: string, v: string): boolean {
  try {
    localStorage.setItem(k, v)
    return true
  } catch {
    return false
  }
}
function drop(k: string) {
  try {
    localStorage.removeItem(k)
  } catch {
    /* nothing to do */
  }
}

/**
 * Split a document into passages.
 *
 * Paragraph-first, then merged up to a floor and split down from a ceiling. The
 * floor matters more than it looks: without it a document of one-line list items
 * yields hundreds of passages that each match a query on one word, and the
 * retrieval fills up with fragments while the paragraph that actually answers the
 * question sits below the cut.
 */
export function passagesOf(text: string): string[] {
  const paras = text
    .replace(/\r\n/g, '\n')
    .split(/\n\s*\n+/)
    .map((p) => p.trim())
    .filter(Boolean)

  const out: string[] = []
  let buf = ''
  const flush = () => {
    if (buf.trim()) out.push(buf.trim())
    buf = ''
  }
  for (const p of paras) {
    if (p.length > MAX_PASSAGE) {
      flush()
      // A wall of text with no blank lines. Split on sentence ends so a passage
      // still begins somewhere a reader would begin.
      let rest = p
      while (rest.length > MAX_PASSAGE) {
        const cut = rest.lastIndexOf('. ', MAX_PASSAGE)
        const at = cut > MIN_PASSAGE ? cut + 1 : MAX_PASSAGE
        out.push(rest.slice(0, at).trim())
        rest = rest.slice(at)
      }
      if (rest.trim()) buf = rest.trim()
      continue
    }
    buf = buf ? `${buf}\n\n${p}` : p
    if (buf.length >= MIN_PASSAGE) flush()
  }
  flush()
  return out
}

/* ── the short forms a document defines ────────────────────────────────────
   A summary that writes "micro- and nanoplastics (MNPs)" once and "MNPs" ever
   after is a document a claim about micro- and nanoplastics should find, and a
   claim naming MNPs should find the paragraph that spelled them out. Keyword
   retrieval cannot know that on its own, but the document said so itself, in the
   one construction English uses for exactly this. */

/** A short form, and the words the document spelled it out as. */
export interface Acronym {
  short: string
  long: string
}

/** Schwartz and Hearst's bound on a long form's length, in words, for a short
 *  form of `n` letters: min(n + 5, 2n). */
const longFormWords = (n: number): number => Math.min(n + 5, n * 2)

/**
 * The words before a parenthesis that spell out `letters`, or null.
 *
 * Schwartz and Hearst (2003), unmodified: match the letters right to left, each
 * anywhere in a word except the first, which must begin one. "MNP" finds
 * "micro- and nanoplastics" — M begins "micro", N and P sit inside
 * "nanoplastics" — and the long form starts at the word the first letter began.
 */
function spelledOut(letters: string, window: string): string | null {
  const lower = window.toLowerCase()
  const wordChar = (c: string | undefined) => c !== undefined && /[\p{L}\p{N}]/u.test(c)
  let l = lower.length - 1
  for (let s = letters.length - 1; s >= 0; s--) {
    while (l >= 0 && (lower[l] !== letters[s] || (s === 0 && wordChar(lower[l - 1])))) l--
    if (l < 0) return null
    l--
  }
  return window.slice(window.lastIndexOf(' ', l) + 1).trim()
}

/**
 * Every "long form (SHORT)" a document writes.
 *
 * Only that shape, and only the conservative reading of it, because what this
 * feeds is a match: a false definition makes the search offer passages that are
 * not about the claim, and a check handed passages will lean on them. So a short
 * form needs two capitals ("(see)" and "(n)" define nothing), must be one word,
 * and is spelled out only by the words of the clause the parenthesis closes —
 * never reaching back across a full stop, a colon or another bracket. A plural
 * short form ("MNPs") is defined by the letters before its s.
 */
export function acronymsOf(text: string): Acronym[] {
  const out: Acronym[] = []
  const seen = new Set<string>()
  for (const m of text.matchAll(/\(\s*([A-Za-z][A-Za-z0-9-]{1,11})\s*\)/g)) {
    const short = m[1]!
    if ((short.match(/[A-Z]/g) ?? []).length < 2) continue
    const letters = (/[A-Z]s$/.test(short) ? short.slice(0, -1) : short)
      .replace(/[^A-Za-z0-9]/g, '')
      .toLowerCase()
    const before = text.slice(Math.max(0, m.index - 400), m.index)
    let from = 0
    for (const b of before.matchAll(/[.!?](?=\s)|[;:()[\]\n]/g)) from = b.index + 1
    const window = before
      .slice(from)
      .trim()
      .split(/\s+/)
      .slice(-longFormWords(letters.length))
      .join(' ')
    const long = spelledOut(letters, window)
    // Longer than what it defines, and not containing it: "the PE (PE)" is not a
    // definition, it is a repetition.
    if (!long || long.length <= letters.length) continue
    if (new RegExp(`(^|[^\\p{L}\\p{N}])${letters}($|[^\\p{L}\\p{N}])`, 'iu').test(long)) continue
    const key = `${short}\u0000${long}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push({ short, long })
  }
  return out
}

/* ── starting from somebody else's report ──────────────────────────────────
   A deep research report is a dense, cited, pre-structured document, which is
   exactly what this corpus is for — and the most useful thing to do with one is to
   argue with its central claim rather than to read it again. `leadClaim` lifts a
   candidate for that claim so the flow is one press; the seed box is where it is
   edited, and nothing is spent until Explore. */

/** Headings a document puts its own answer under. */
const VERDICT_HEADING =
  /\b(verdicts?|conclusions?|bottom line|summary|abstract|answers?|thesis|key findings?|executive)\b/i

/** Sentence openers that continue an argument rather than stating one. */
const CONTINUES =
  /^(and|but|or|so|yet|however|therefore|thus|moreover|furthermore|in addition|for example|e\.g\.|i\.e\.|that said|instead)\b/i

/** Prose about the document rather than about the world. */
const ABOUT_ITSELF =
  /\b(this (report|document|analysis|section|paper|review)|we (searched|reviewed|surveyed|note)|see (section|below|above)|as (noted|described|shown) (above|below)|table \d|figure \d)\b/i

/**
 * A heading standing alone on its line, as a PDF's text layer or a plain paste
 * carries one — no `#` in front of it. Numbered ("5. Conclusion", "5 Conclusions",
 * "4.2 Key findings", "V. Conclusion"): a section number, then at most eight words
 * opening on a capital, with nothing that ends a sentence. Unnumbered, only a
 * section's own name ("Conclusion", "CONCLUSIONS", "Key findings", "References") in
 * at most four words, because a short capitalised line is otherwise as likely to be
 * a title or a table label as a heading, and a guess here moves what is lifted.
 */
const NUMBERED_HEADING = /^(?:\d{1,2}(?:\.\d{1,2})*\.?|[IVXLC]{1,6}\.)\s+([A-Z][^.!?;]*?):?$/
const SECTION_HEADING =
  /^(?:introduction|background|methods?|methodology|materials and methods|results|discussion|limitations|references|bibliography|acknowledge?ments|appendix|appendices|funding|notes|contents)$/i
const HEADING_WORDS_NUMBERED = 8
const HEADING_WORDS_BARE = 4

/** The heading's words when `line` is a standalone heading, else null. */
function standaloneHeading(line: string): string | null {
  const t = line.replace(/\*\*?|__/g, '').trim()
  const numbered = NUMBERED_HEADING.exec(t)
  if (numbered) {
    const words = numbered[1]!.trim()
    return words.split(/\s+/).length <= HEADING_WORDS_NUMBERED ? words : null
  }
  const bare = t.replace(/:$/, '')
  if (!/^[A-Z]/.test(bare) || /[.!?;,]$/.test(bare)) return null
  if (bare.split(/\s+/).length > HEADING_WORDS_BARE) return null
  return SECTION_HEADING.test(bare) || VERDICT_HEADING.test(bare) ? bare : null
}

/**
 * Lines about the DOCUMENT — who it is by, for, from and when — which are never its
 * claim, however much they look like a sentence. "Prepared for an environmental-health
 * journal club, September 2026." has a full stop, seven words and forty characters, so
 * `readsAsClaim` passed it, and on a document whose pages came out as one line each it
 * was the first sentence the finder met. Skipped by their opening words or their
 * shape, never by a date appearing inside prose: "By 2030 the grid will …" is a claim.
 */
const METADATA: readonly RegExp[] = [
  // "Prepared for …", "Written by …", "Submitted to …", "Presented at …"
  /^(?:prepared|written|compiled|produced|submitted|presented|commissioned|drafted|reviewed|edited|updated|revised)\s+(?:for|by|to|at|on|in)\b/i,
  // A labelled field: "Date:", "Authors:", "Keywords:", "DOI:" …
  /^(?:date|dated|authors?|affiliations?|correspondence|contact|keywords?|doi|version|draft|status|received|accepted|published|citation|cite as|funding|disclosures?)\s*:/i,
  // A byline: "By Ana Ruiz", "By A. Ruiz and B. Chen", "By Ana Ruiz, University of …"
  /^[Bb]y\s+(?:[A-Z][a-z]+|[A-Z]\.)(?:\s+(?:[A-Z][a-z'’-]+|[A-Z]\.))+\s*(?:,|\band\b|$)/,
  // An affiliation line.
  /^(?:department|dept\.|school|faculty|institute|university|centre|center|division|laboratory)\s+(?:of|for)\b/i,
  // An address, a copyright line.
  /\S+@\S+\.\w+|©|\bcopyright\b|\ball rights reserved\b/i,
  // A line that is only a date: "September 2026", "25 September 2026", "2026-09-25".
  /^(?:\d{1,2}\s+)?(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+(?:\d{1,2},\s*)?\d{4}\.?$/i,
  /^\d{4}-\d{2}-\d{2}\.?$/,
]
/**
 * A line of names and nothing else — "A. Ruiz and B. Chen", "Ana Ruiz, Bernard Chen" —
 * which is an author list under a title. Every word a capitalised name or an initial,
 * joined only by commas, "and" or "&", and short. A title set in Title Case reads the
 * same way and is skipped with it, which costs nothing: a title is not a claim either.
 */
const NAME_TOKEN = /^(?:(?:[A-Z]\.)+|[A-Z][a-z'’-]+|and|&)$/
const isNameList = (line: string): boolean => {
  const tokens = line
    .replace(/[,;*†‡¹²³\d]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
  return (
    tokens.length >= 2 &&
    tokens.length <= 16 &&
    tokens.every((t) => NAME_TOKEN.test(t)) &&
    tokens.some((t) => /^[A-Z][a-z]/.test(t))
  )
}
const isMetadata = (line: string): boolean =>
  METADATA.some((re) => re.test(line)) || isNameList(line)

/** A Markdown list item: it starts a block of its own rather than continuing one. */
const LIST_ITEM = /^\s*(?:[-*+]|\d+[.)])\s+/

/**
 * Whether `next` continues the paragraph whose last line is `prev`.
 *
 * A PDF's text layer breaks a paragraph at every line of the page, and Markdown lets
 * a paragraph wrap too, so a sentence can span lines and the finder has to put them
 * back together. What it must NOT do is weld a title or a label onto the sentence
 * under it — "Microplastics in drinking water: a review Bottled water carries …" ends
 * in a full stop and would read as a claim. So a line joins the one before it when it
 * opens in lower case (a sentence carried over the break), or when the one before
 * ended a sentence (the next one in the same paragraph, which the splitter parts
 * again). A line that opens on a capital after one that ended mid-flow starts a block
 * of its own; the price is a wrapped sentence whose second line opens on a name,
 * which is then not lifted — the direction this finder errs in on purpose.
 */
const continuesLine = (prev: string, next: string): boolean =>
  /^[a-z(“"‘'—–-]/.test(next) || /[.!?]["”’)]?$/.test(prev)

/** Two wrapped lines as one: a word hyphenated across the break is mended. */
const joinWrapped = (prev: string, next: string): string =>
  /[a-z]-$/.test(prev) && /^[a-z]/.test(next) ? `${prev.slice(0, -1)}${next}` : `${prev} ${next}`

/** A claim short enough to be a seed and long enough to be one. The seed is clamped
 *  to 400 characters by the codec and becomes a title in five places. */
const CLAIM_MIN = 40
const CLAIM_MAX = 240
/** Words below which a span is a caption or a label rather than a proposition. */
const CLAIM_WORDS = 7

/** One line of Markdown as its words. */
function plainLine(s: string): string {
  return (
    s
      .replace(/^\s*([-*+]|\d+[.)])\s+/, '')
      .replace(/^\s*>+\s*/, '')
      .replace(/`([^`]*)`/g, '$1')
      .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
      // Bracketed citation markers, which reports are full of and which would land in a
      // title as "[12]".
      .replace(/\[\d+(?:[,–-]\s*\d+)*\]/g, '')
      .replace(/\*\*?|__|~~/g, '')
      .replace(/\s{2,}/g, ' ')
      // After the deletions above, which leave a space in front of the punctuation they
      // took a citation marker out from.
      .replace(/\s+([.,;:!?])/g, '$1')
      .trim()
  )
}

/**
 * Sentence boundaries, over prose full of decimals.
 *
 * The lookahead requires whitespace after the stop, so `0.074` is one token and a
 * report's every figure does not become a sentence break. `Fig. 3` does split, which is
 * the acceptable half of the trade.
 */
const sentencesOf = (line: string): string[] =>
  line
    .split(/(?<=[.!?])\s+(?=["“(]?[A-Z0-9])/)
    .map((s) => s.trim())
    .filter(Boolean)

/**
 * Whether a span reads as a proposition — something that could be true or false.
 *
 * Deliberately structural, and deliberately NOT a list of verbs. The first version of
 * this was exactly that, and it failed on the first real sentence it met: "confidence
 * TRACKS accuracy" is a claim and `tracks` was not on the list, so the whole flow
 * silently produced nothing. English has too many verbs for a whitelist to be anything
 * but a source of quiet misses.
 *
 * What is left is four facts about the span. A terminal full stop does most of the work:
 * headings, captions, table cells and bare list labels overwhelmingly lack one, and a
 * sentence has one. Erring toward NO suggestion is the right direction — the box is
 * empty either way, and a bad suggestion is worse than none.
 */
const readsAsClaim = (s: string): boolean =>
  s.length >= CLAIM_MIN &&
  s.length <= CLAIM_MAX &&
  /[.!]["”)]?$/.test(s) &&
  s.split(/\s+/).length >= CLAIM_WORDS &&
  !CONTINUES.test(s) &&
  !ABOUT_ITSELF.test(s)

/**
 * The claim a document appears to be arguing for, or null.
 *
 * A SUGGESTION and never a decision, exactly like `suggestFrame`: it fills the seed box
 * and the person edits it before anything is spent. Silently seeding an exploration with
 * a sentence a regex picked would be the tool deciding what somebody meant to argue
 * with, which is the one judgement this app leaves to its user everywhere else.
 *
 * Two passes and no scoring, deliberately. A weighted heuristic here would be
 * unreadable and unfalsifiable, and it would still be wrong sometimes — so instead:
 * the first sentence that reads as a claim under a heading the document itself marked
 * as its answer, and failing that the first anywhere. Both are rules a reader can
 * check against their own document.
 *
 * A heading is a Markdown `#` line or a standalone one (`standaloneHeading`) — the
 * form a PDF's "5. Conclusion" takes, now that its lines survive extraction. The
 * document's own metadata is never a candidate (`isMetadata`), and a paragraph
 * wrapped across lines is read as one before its sentences are split.
 */
export function leadClaim(text: string): string | null {
  const underVerdict: string[] = []
  const rest: string[] = []
  let fenced = false
  let inVerdict = false
  // The paragraph being put back together, as plain text, with its last line kept
  // apart so the next one can be asked whether it continues it.
  let para = ''
  let lastLine = ''
  const flush = () => {
    if (para) (inVerdict ? underVerdict : rest).push(para)
    para = ''
    lastLine = ''
  }

  for (const raw of text.replace(/\r\n/g, '\n').split('\n')) {
    // A code block is not anybody's thesis.
    if (/^\s*(```|~~~)/.test(raw)) {
      flush()
      fenced = !fenced
      continue
    }
    if (fenced) continue
    // A blank line ends a paragraph, as it does in Markdown and between PDF pages.
    if (!raw.trim()) {
      flush()
      continue
    }
    const marked = /^\s{0,3}#{1,6}\s+(.*)$/.exec(raw)
    const heading = marked ? marked[1]! : standaloneHeading(raw)
    if (heading !== null) {
      flush()
      inVerdict = VERDICT_HEADING.test(heading)
      continue
    }
    const line = plainLine(raw)
    if (!line) continue
    if (isMetadata(line)) {
      flush()
      continue
    }
    if (para && (LIST_ITEM.test(raw) || !continuesLine(lastLine, line))) flush()
    para = para ? joinWrapped(para, line) : line
    lastLine = line
  }
  flush()

  for (const pool of [underVerdict, rest]) {
    for (const block of pool) {
      for (const s of sentencesOf(block)) if (readsAsClaim(s)) return s
    }
  }
  return null
}

function readIndex(): CorpusDoc[] {
  const raw = get(K.index)
  if (!raw) return []
  try {
    const d = JSON.parse(raw)
    if (!Array.isArray(d)) return []
    return (
      (d as CorpusDoc[])
        .filter((e) => e && typeof e.id === 'string' && typeof e.title === 'string')
        .map((e) => ({
          id: e.id,
          title: e.title,
          addedAt: Number(e.addedAt) || Date.now(),
          chars: Math.max(0, Math.trunc(Number(e.chars) || 0)),
          passages: Math.max(0, Math.trunc(Number(e.passages) || 0)),
          chunker: Number(e.chunker) || 0,
        }))
        // An index entry whose text key is gone is a dead row. Dropped on read
        // rather than repaired, because there is nothing to repair it from.
        .filter((e) => get(K.doc(e.id)) !== null)
    )
  } catch {
    return []
  }
}

function writeIndex(items: CorpusDoc[]): boolean {
  return put(K.index, JSON.stringify(items))
}

export function listDocs(): CorpusDoc[] {
  return readIndex().sort((a, b) => b.addedAt - a.addedAt)
}

export function corpusChars(): number {
  return readIndex().reduce((n, e) => n + e.chars, 0)
}

export function loadDoc(id: string): LoadedDoc | null {
  const raw = get(K.doc(id))
  if (!raw) return null
  try {
    const d = JSON.parse(raw) as Record<string, unknown>
    const text = typeof d.text === 'string' ? d.text : ''
    if (!text) return null
    const chunks = passagesOf(text)
    return {
      id,
      title: typeof d.title === 'string' ? d.title : 'Untitled',
      addedAt: Number(d.addedAt) || Date.now(),
      // Recomputed, never read from the index.
      chars: text.length,
      passages: chunks.length,
      chunker: Number(d.chunker) || 0,
      text,
      chunks,
      acronyms: acronymsOf(text),
    }
  } catch {
    return null
  }
}

/** Every document, loaded and chunked. Called once per check. */
export function loadCorpus(): LoadedDoc[] {
  return readIndex()
    .map((e) => loadDoc(e.id))
    .filter((d): d is LoadedDoc => !!d)
}

export type AddResult = { ok: true; doc: CorpusDoc } | { ok: false; why: string }

/**
 * Add a document.
 *
 * Every refusal states the arithmetic. "Too large" with no numbers leaves the
 * user to guess at a limit they cannot see, and the obvious guess — delete
 * everything and try again — is the one action that loses work.
 */
export function addDoc(title: string, text: string): AddResult {
  const clean = text.replace(/\r\n/g, '\n').trim()
  if (clean.length < 40) {
    return { ok: false, why: 'That is too short to be worth searching — under 40 characters.' }
  }
  if (clean.length > DOC_CHARS) {
    return {
      ok: false,
      why: `That document is ${clean.length.toLocaleString()} characters and the limit is ${DOC_CHARS.toLocaleString()}. Split it, or paste the part that matters.`,
    }
  }

  const items = readIndex()
  if (items.length >= MAX_DOCS) {
    return {
      ok: false,
      why: `${MAX_DOCS} documents is the limit. Remove one to add another.`,
    }
  }
  const used = items.reduce((n, e) => n + e.chars, 0)
  if (used + clean.length > CORPUS_CHARS) {
    const room = Math.max(0, CORPUS_CHARS - used)
    return {
      ok: false,
      why: `Your sources hold ${used.toLocaleString()} of ${CORPUS_CHARS.toLocaleString()} characters, so there is room for ${room.toLocaleString()} more and this is ${clean.length.toLocaleString()}. Remove a document first.`,
    }
  }
  // A duplicate is almost always a double paste, and a corpus holding the same
  // passage twice gives that passage two chances to be retrieved.
  for (const e of items) {
    const d = loadDoc(e.id)
    if (d && d.text === clean) {
      return { ok: false, why: `That is the same text as “${e.title}”, which you already added.` }
    }
  }

  const id = uid('c')
  const chunks = passagesOf(clean)
  const doc: CorpusDoc = {
    id,
    title: title.trim().slice(0, 120) || 'Untitled',
    addedAt: Date.now(),
    chars: clean.length,
    passages: chunks.length,
    chunker: CHUNKER,
  }
  // Text first, index second. An index entry whose text key was never written is
  // a row that promises a document nobody can read; the reverse is invisible.
  if (
    !put(
      K.doc(id),
      JSON.stringify({
        v: 1,
        title: doc.title,
        addedAt: doc.addedAt,
        chunker: CHUNKER,
        text: clean,
      }),
    )
  ) {
    return { ok: false, why: 'This browser refused the write — its storage is full.' }
  }
  if (!writeIndex([...items, doc])) {
    drop(K.doc(id))
    return { ok: false, why: 'This browser refused the write — its storage is full.' }
  }
  return { ok: true, doc }
}

export function removeDoc(id: string): void {
  drop(K.doc(id))
  writeIndex(readIndex().filter((e) => e.id !== id))
}
